import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Input, ScrollArea } from '../ui';
import { CountryOption } from '../../../lib/country-codes';
import { useTranslation } from '../../../lib/i18n';
import styles from './CountrySelect.module.css';

// ARIA APG "combobox with listbox popup": once open, the SEARCH FIELD is the combobox
// and owns the keyboard — the options are never tab stops (193 of them would make
// leaving the control take ~194 Tabs) and the active one is named through
// `aria-activedescendant`, which is the only way arrowing a filtered list announces
// anything. Closing always hands focus back to the trigger; dropping it to <body>
// would strand a keyboard user at the top of the page.
export function CountrySelect({
  countries,
  value,
  onChange,
  onOpen,
  disabled,
}: {
  countries: CountryOption[];
  /** Selected ISO code. Keyed on ISO, not dial — +1 is US, CA and more. */
  value: string;
  onChange: (iso: string) => void;
  onOpen?: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const selected = countries.find((c) => c.iso === value);
  const matches = useMemo(() => filterCountries(countries, query), [countries, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, matches.length]);

  function openList() {
    // Reset here rather than in an effect on `open`: an effect would set the cursor to
    // the selected country and then be immediately clobbered by the `query` reset
    // firing the filter's own reset — which is why reopening used to land on Afghanistan.
    setQuery('');
    setActiveIndex(Math.max(0, countries.findIndex((c) => c.iso === value)));
    setOpen(true);
    onOpen?.();
  }

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function pick(iso: string) {
    onChange(iso);
    close();
  }

  function onQueryChange(next: string) {
    setQuery(next);
    setActiveIndex(0);
  }

  function onListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => Math.min(matches.length - 1, Math.max(0, i + step)));
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActiveIndex(event.key === 'Home' ? 0 : matches.length - 1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const match = matches[activeIndex];
      if (match) pick(match.iso);
    }
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('onboarding.phone.countryCode.label')}
        className={styles.trigger}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            openList();
          }
        }}
      >
        <span className={styles.flag} aria-hidden>
          {selected?.flag}
        </span>
        {/* One text node, not `+{dial}` — React would emit a comment between them, which
            splits the bidi run and renders the lone `+` after the digits ("972+") in an
            RTL locale. */}
        <span className={styles.dial}>{`+${selected?.dial ?? ''}`}</span>
        <ChevronDown className={styles.chevron} size={16} aria-hidden />
      </button>

      {open && (
        <div className={styles.popover} onKeyDown={onListKeyDown}>
          <Input
            autoFocus
            role="combobox"
            aria-expanded
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={matches[activeIndex] ? optionId(activeIndex) : undefined}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t('onboarding.phone.countrySearch.placeholder')}
            aria-label={t('onboarding.phone.countrySearch.placeholder')}
            startIcon={<Search size={16} aria-hidden />}
          />
          {matches.length === 0 ? (
            <div className={styles.empty}>{t('onboarding.phone.countrySearch.empty')}</div>
          ) : (
            <ScrollArea component="ul" className={styles.list} id={listboxId} role="listbox" ref={listRef}>
              {matches.map((country, index) => (
                <li
                  key={country.iso}
                  id={optionId(index)}
                  role="option"
                  aria-selected={country.iso === value}
                  className={`${styles.option} ${index === activeIndex ? styles.active : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(country.iso)}
                >
                  <span className={styles.flag} aria-hidden>
                    {country.flag}
                  </span>
                  {/* Node's ICU and the browser's spell some regions differently in the same
                      locale, so region names are display-only and never hydration-critical. */}
                  <span className={styles.optionName} suppressHydrationWarning>
                    {country.name}
                  </span>
                  <span className={styles.optionDial}>{`+${country.dial}`}</span>
                </li>
              ))}
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}

// Matches on country name or dial code, with or without the leading '+', so both
// "Israel" and "+972" find Israel.
function filterCountries(countries: CountryOption[], query: string): CountryOption[] {
  const needle = query.trim().toLowerCase().replace(/^\+/, '');
  if (!needle) return countries;
  return countries.filter(
    (c) => c.name.toLowerCase().includes(needle) || c.dial.startsWith(needle) || c.iso.toLowerCase() === needle,
  );
}
